import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { decodeJWTPayload } from '../../utils/jwt';

interface BackendRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route component for backend pages (admin or developer)
 * Admin: All permissions
 * Developer: All admin permissions except Stripe config
 * No public backend routes - all backend access is dynamically determined
 */
export function BackendRoute({ children }: BackendRouteProps) {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkBackendAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.warn('[BackendRoute] No session, redirecting to signin');
          navigate('/signin');
          return;
        }

        // Extract JWT claims (is_admin and is_developer are set by custom_access_token_hook)
        // The hook checks the admins and developer_profiles tables and sets claims
        const claims = session.access_token ? decodeJWTPayload(session.access_token) : null;
        let isAdmin = claims?.is_admin === true;
        let isDeveloper = claims?.is_developer === true;

        // If JWT claims aren't set yet (common right after login), check database directly
        if (!isAdmin && !isDeveloper) {
          console.warn('[BackendRoute] JWT claims not set, checking database directly...');
          
          // Check admin status
          const { data: adminCheck } = await supabase
            .from('admins')
            .select('user_id')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          if (adminCheck) {
            isAdmin = true;
          }

          // Check developer status
          if (!isAdmin) {
            const { data: devCheck } = await supabase
              .from('developer_profiles')
              .select('id, is_active')
              .eq('id', session.user.id)
              .maybeSingle();
            
            if (devCheck?.is_active) {
              isDeveloper = true;
            }
          }
        }

        if (!isAdmin && !isDeveloper) {
          console.warn('[BackendRoute] User is not an admin or developer, redirecting to dashboard');
          navigate('/dashboard');
          return;
        }

        setIsAuthorized(true);
      } catch (err) {
        console.error('[BackendRoute] Error checking backend access:', err);
        navigate('/dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    checkBackendAccess();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
      </div>
    );
  }

  if (!isAuthorized) {
    return null; // Will redirect
  }

  return children;
}
